import { SUPABASE_PUBLISHABLE_KEY } from './supabase';
import { fetchWithTimeout } from './bookSearch';

// Deliberately short, because this is multiplied. resolveAllTagsToBookTags
// resolves every tag on a book one after another (:496-500), and a reader save
// needs at least three, so a per-call deadline of N seconds is a worst case of
// 3N or more on one save, with the reader watching a spinner.
//
// Giving up early costs almost nothing here: resolveTagToTropeId falls through
// to fallbackTropeLookup, a direct query against our own database, which is
// both fast and the answer we want when the edge function is unreachable.
//
// This is the OUTER deadline of a pair. It has to stay above process-trope's
// own OPENAI_EMBED_TIMEOUT_MS (5 s, supabase/functions/process-trope/index.ts)
// plus the work the function does around that call: steps 2-4 before it, and
// the match_tropes search and two or three writes after it. If it falls to that
// or below, the browser hangs up first and the function's limit can never be
// the one that ends the wait. scripts/check-deadline-nesting.mjs checks only
// the first part, that this is above the 5 s.
const PROCESS_TROPE_TIMEOUT_MS = 8000;

// A tag is a label, not a sentence.
//
// Three rows in the live catalog are whole paragraphs somebody typed into a
// tag field — the longest runs to 224 characters and begins "Some sex
// that is open door, but it's not 'spice' per se...". Each one is a permanent
// trope row that nothing will ever match against, because no second person
// types the same paragraph.
//
// 60 admits every real tag in the catalog. The longest legitimate names in the
// flagged-review backlog are "miscarriage and stillbirth briefly mentioned
// (not FMC)" at 54 and "Harming Others for the Use of Blood Magic" at 41. The
// only names it turns away are the ones that should always have been shorter.
export const MAX_TAG_NAME_LENGTH = 60;

/**
 * Returns a message to show the person, or null when the name is fine.
 *
 * Deliberately not a maxLength attribute on the input. maxLength truncates on
 * paste with no explanation, so "Dubious consent elements (consensual, but
 * with pressure and imbalance)" would silently become a tag cut off mid-word —
 * and a silently mangled tag is worse than a refused one, because nobody can
 * see it happened. Refusing visibly and leaving their text where it is lets
 * them shorten it themselves.
 */
export function tagNameError(name) {
  const clean = (name || '').trim();
  if (!clean) return null;
  if (clean.length > MAX_TAG_NAME_LENGTH) {
    return `Keep tags short, ${MAX_TAG_NAME_LENGTH} characters or fewer. This one is ${clean.length}.`;
  }
  return null;
}

/**
 * tropeUtils.js
 *
 * Shared utility for routing any tag submission through the
 * process-trope edge function. Used by Add5StarReadPopup,
 * CSVUploadModal, and any other place tags are submitted.
 *
 * The edge function handles:
 *   ≥90%  → auto-merge (saves alias, returns canonical trope_id)
 *   75-89% → flags for admin review (returns pending trope_id)
 *   <75%   → new trope submitted (returns pending trope_id)
 *   alias  → maps to canonical immediately (returns canonical trope_id)
 *   exact  → returns canonical trope_id directly
 *
 * For book_tags:
 *   - alias/exact/auto_merged → tag the book with the canonical trope_id
 *   - flagged_review/new_trope → tag with the pending trope_id (admin
 *     approves later, book_tag already exists for when it goes live)
 */

import { supabase } from './supabase';

/**
 * Infer the tag category from its type string.
 * The edge function groups by category for similarity matching.
 */
function inferCategory(tagType) {
  const map = {
    trope:   'trope',
    genre:   'genre',
    tone:    'tone',
    spice:   'spice',
    trigger: 'trigger',
  };
  return map[tagType] || 'trope';
}

/**
 * Escapes ILIKE/PostgREST wildcard characters in a user-provided name before
 * it goes into a `.ilike()` filter. \, %, _ are Postgres LIKE wildcards;
 * PostgREST additionally treats * as %, so an unescaped * would silently
 * match via prefix/suffix wildcarding instead of being searched for
 * literally — the trope lookup would then bind to the wrong trope_id with
 * no visible error.
 */
export function escapeLike(name) {
  return name.trim().replace(/[\\%_*]/g, m => '\\' + m);
}

/**
 * Process a single tag through the edge function.
 *
 * Returns { trope_id, result_type } where:
 *   trope_id   — the ID to use for book_tags (canonical or pending)
 *   result_type — 'alias_match' | 'exact_match' | 'auto_merged' |
 *                 'flagged_review' | 'new_trope' | null (on error)
 *
 * Returns null if the edge function fails — callers should fall back
 * to a direct trope lookup in that case.
 */
export async function processTagThroughEdgeFunction(tagName, tagType = 'trope') {
  if (!tagName?.trim()) return null;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('processTag: no session');
      return null;
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-trope`;
    console.log(`processTag calling: ${url} with "${tagName}"`);

    const res = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        trope_name: tagName.trim(),
        category:   inferCategory(tagType),
      }),
    }, PROCESS_TROPE_TIMEOUT_MS);

    console.log(`processTag response status: ${res.status} for "${tagName}"`);

    if (!res.ok) {
      const body = await res.text();
      console.warn(`processTag failed: ${res.status} — ${body}`);
      return null;
    }

    const data = await res.json();
    console.log(`processTag result for "${tagName}":`, data);
    // possible_match is carried through because on a flagged_review the
    // trope_id points at a row process-trope JUST created, whose category is
    // whatever this call sent — so reading a bucket off it only reads back our
    // own guess. possible_match names the existing tag it was flagged against,
    // and that one has a category a person actually chose. See
    // resolveTagWithBucket.
    return {
      trope_id:       data.trope_id || null,
      result_type:    data.result || null,
      possible_match: data.possible_match || null,
    };

  } catch (err) {
    console.warn(`processTag exception for "${tagName}":`, err.message);
    return null;
  }
}

/**
 * Fallback: look up a trope by exact or fuzzy name directly in the DB.
 * Used when the edge function is unavailable (503 etc).
 */
async function fallbackTropeLookup(tagName) {
  const escaped = escapeLike(tagName);

  // 1. Try exact match on canonical_name
  //
  // Order by id ascending and take the first: where several casings of the
  // same trope already exist, this deterministically picks the oldest row
  // rather than an arbitrary one, so repeated calls converge on one winner
  // instead of scattering tags across duplicates.
  const { data: exact } = await supabase
    .from('tropes')
    .select('id')
    .ilike('canonical_name', escaped)
    .eq('is_active', true)
    .order('id', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (exact?.id) return exact.id;

  // 2. Try alias match
  //
  // Only a switched-on tag, the same rule as the exact step above (ticket 76).
  // An alias can still point at a MERGED tag -- merges made before merge_tropes
  // moved a merged tag's aliases to the kept one
  // (20260819010000_sweep_blockers.sql, step 07) left them behind -- and without
  // this check the alias hands that merged tag back. This fallback runs whenever
  // process-trope fails or returns no id, so it would tag a book with a tag that
  // doesn't show for a reader filtering by that name. `trope:tropes(is_active)`
  // reads the tag the alias points at; an alias is unique across every tag
  // (trope_aliases_alias_key), so it is one row to check. A merged tag returns
  // null, like a name nobody knows, and the caller treats it as unresolved.
  const { data: alias } = await supabase
    .from('trope_aliases')
    .select('trope_id, trope:tropes(is_active)')
    .ilike('alias', escaped.toLowerCase())
    .order('trope_id', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (alias?.trope_id && alias.trope?.is_active === true) return alias.trope_id;

  return null;
}

/**
 * High-level function: process a tag and return the trope_id to use
 * for a book_tags insert. Handles fallback automatically.
 *
 * @param {string} tagName    — the raw tag string from AI or user
 * @param {string} tagType    — 'trope' | 'genre' | 'tone' | 'spice' | 'trigger'
 * @returns {number|null}     — trope_id, or null if not resolvable
 */
export async function resolveTagToTropeId(tagName, tagType = 'trope') {
  // Try edge function first
  const result = await processTagThroughEdgeFunction(tagName, tagType);

  if (result?.trope_id) {
    return result.trope_id;
  }

  // Edge function failed or returned no ID — fall back to direct DB lookup
  const fallbackId = await fallbackTropeLookup(tagName);
  return fallbackId;
}

/**
 * Submit a bare trope/genre name to the catalogue, with no book attached.
 *
 * This is for the places where a reader names a trope that is not about one
 * specific book — My Top Tropes, the profile's custom genre, and the "these
 * tropes are new, put them in front of an admin" step after a Recommend Me or
 * To Know Me save. Those all used to do the same thing:
 *
 *   look it up; if it is not there, INSERT it with is_active false
 *
 * which is a blind create. It minted "HEA" beside an existing "Happily Ever
 * After" with nothing comparing the two, and every one of those rows landed in
 * the admin queue for a human to merge by hand. process-trope already decides
 * that question — alias it, merge it, flag it, or create it — so the name goes
 * there instead and no caller creates rows itself.
 *
 * Returns { ok: true, trope_id, result_type } or { ok: false, reason }.
 *
 * On failure it deliberately does NOT fall back to inserting the row. A blind
 * insert is the exact thing being removed, and an unmatched duplicate is worse
 * than a name that did not reach the queue: the reader keeps their trope on
 * their profile either way, and the next submission retries it.
 */
export async function submitTropeName(name, tagType = 'trope') {
  const clean = name?.trim();
  if (!clean) return { ok: false, reason: 'empty name' };

  const result = await processTagThroughEdgeFunction(clean, tagType);
  if (result?.trope_id) {
    return { ok: true, trope_id: result.trope_id, result_type: result.result_type };
  }

  // processTagThroughEdgeFunction already logged the specific cause.
  console.warn(`submitTropeName: "${clean}" did not reach the trope queue — the matcher did not answer. Not creating a row for it.`);
  return { ok: false, reason: 'matcher unavailable' };
}

/**
 * `tropes.category` -> the `book_tags.tag_type` it belongs under.
 *
 * Two different columns with overlapping values, so the map is mostly identity
 * plus the five legacy Title Case values that predate the eight buckets. Those
 * five looked like a layer underneath `trope` and never were one — see
 * src/lib/tagBuckets.js. `category` says what kind of tag a name is, and
 * nothing else; there is no level below the eight and none is being added.
 * `book_tags_tag_type_check` permits exactly the eight on the right, so an
 * unmapped category must NOT be passed through — Postgres would reject the
 * insert and the tag would be lost.
 */
const CATEGORY_TO_TAG_TYPE = {
  trope:                     'trope',
  'Relationship Dynamics':   'trope',
  'Characters & Situations': 'trope',
  'Plot Devices':            'trope',
  'Fantasy Elements':        'trope',
  genre:                     'genre',
  tone:                      'tone',
  'Tone & Heat':             'tone',
  spice:                     'spice',
  trigger:                   'trigger',
  pov:                       'pov',
  setting:                   'setting',
  Representation:            'representation',
};

/**
 * Which bucket does a name a reader typed belong in? Ask the matcher.
 *
 * WHAT THIS REPLACES
 *
 * The reader's profile blocks — "To Know Me", "If You Like What I Like", the
 * book tag picker — never ask which of the eight buckets a tag belongs to. The
 * UI has no such control, deliberately: a reader naming what they love should
 * not have to file it. So the code had to decide, and it decided like this:
 *
 *     if (GENRE_NAMES.includes(name)) return 'genre';
 *     if (MOOD_NAMES.includes(name))  return 'tone';
 *     return 'trope';
 *
 * Two hardcoded lists of nineteen and thirteen names, and everything else is a
 * trope. So "graphic violence" was a trope. "sapphic" was a trope. "Nigeria"
 * was a trope. Filed under the wrong umbrella, and then only findable there.
 *
 * WHAT IT DOES INSTEAD, AND WHY IT IS NOT A GUESS
 *
 * The matcher already answers this. It takes the typed name through alias, then
 * exact, then near-spelling, then meaning, and hands back the tag it landed on.
 * That tag has a category, because an author already said what it is. So:
 *
 *   "Enemies to Lovers" -> matches the existing tag -> that tag is a trope
 *   "graphic violence"  -> matches "Graphic Gore"   -> that tag is a trigger
 *   "sapphic"           -> matches "LGBTQ+ MC"      -> representation
 *
 * Copying an answer somebody already gave, not inventing one. And the meaning
 * step searches across every bucket already — process-trope passes
 * `filter_category: null` at step 5 — so nothing here narrows what it can find.
 *
 * ONE BUCKET PER TAG PER BOOK, AND THAT IS THE DATABASE'S RULE, NOT A CHOICE
 *
 * A tag can belong to several buckets in the abstract: the author form lists
 * `Dystopian` under genres AND settings. But `book_tags` carries
 * `UNIQUE (book_id, trope_id)` with no tag_type in it
 * (20260811000000_live_schema_snapshot.sql:3473), so one book can hold a given
 * tag exactly once, under exactly one bucket. Writing it twice is not a design
 * decision anyone can make — Postgres refuses the second row.
 *
 * So this returns the single bucket the tag actually lives in. The plural view
 * belongs on the read side, where a name can be offered under every bucket it
 * plausibly fits without any book having to store it twice.
 *
 * (helper for the flagged-review path below — see the long note there)
 *
 * The bucket an EXISTING tag lives in, looked up by its canonical name, or
 * null when there is no live row, no readable category, or the category is not
 * one of the eight.
 *
 * Matched with .eq() rather than .ilike(): process-trope hands back
 * `possible_match` as the stored `canonical_name` verbatim, so there is no
 * casing to normalise, and .ilike() would additionally need the user-input
 * escaping this value does not require. Ordered and limited before
 * .maybeSingle() because `tropes_canonical_name_key` is case-sensitive, so two
 * casings of one name can both exist and .maybeSingle() ERRORS on two rows
 * rather than picking one.
 */
async function bucketForTagName(canonicalName) {
  const { data, error } = await supabase
    .from('tropes')
    .select('category')
    .eq('canonical_name', canonicalName)
    .order('id', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn(`bucketForTagName: could not read the category for "${canonicalName}" (${error.message})`);
    return null;
  }
  return CATEGORY_TO_TAG_TYPE[data?.category] || null;
}

/**
 * A FLAGGED NEAR-MATCH MUST NOT READ ITS BUCKET OFF ITS OWN NEW ROW
 *
 * Found in review by Robert, and it is the same bug this function exists to
 * fix, coming back through a door nobody had looked at.
 *
 * When a typed name scores 75–88% fuzzy or 75–89% by meaning against an
 * existing tag, process-trope does NOT point the book at that tag. It CREATES
 * a new pending row — `insertTrope({ canonical_name, category, ... })` at
 * `process-trope/index.ts:610` and `:687` — where `category` is whatever this
 * request sent, and returns THAT row's id.
 *
 * This function sends no category hint, so the request carries the 'trope'
 * default. Reading a bucket back off the returned id therefore reads back our
 * own guess, one call later, and reporting it as 'matched' claims a considered
 * answer for something nobody considered.
 *
 * Concretely: a reader types "F/F romance", it scores 80% against the genre
 * "LGBTQ+ Romance", and the book gets filed under `trope` — exactly the
 * mis-bucketing this whole change removes.
 *
 * So on a flagged_review the bucket is taken from `possible_match`, the
 * EXISTING tag it was flagged against, whose category a person chose. That is
 * still copying somebody's answer rather than inventing one; it is just
 * copying it from the right row.
 *
 * WHAT IT STILL CANNOT DO
 *
 * A word nobody has ever used matches nothing, so there is no existing answer
 * to copy and it falls back to 'trope' — the old behaviour, for that case only.
 *
 * **And the new pending row keeps `category: 'trope'` regardless.** This fixes
 * what the BOOK is tagged as; it cannot fix what the new row was created as,
 * because that category is chosen inside process-trope before any of this runs.
 * So a flagged near-match of a genre still lands in the admin trope queue
 * carrying 'trope', for a person to correct when they rule on it. Fixing that
 * proper needs an edge-function change, and nothing in this project can deploy
 * an edge function (no supabase/config.toml, no CI, no deploy script), so it is
 * deliberately out of scope here rather than quietly attempted.
 *
 * @returns {{ trope_id: number|null, tagType: string, source: string }}
 *          `source` says where the bucket came from:
 *          'matched'    — read off the row the matcher landed on
 *          'near-match' — borrowed from the tag a flagged review was flagged
 *                         against, because the landed-on row is one we caused
 *          'default'    — nothing to copy, or the lookup failed
 */
export async function resolveTagWithBucket(tagName) {
  const clean = tagName?.trim();
  if (!clean) return { trope_id: null, tagType: 'trope', source: 'default' };

  const matched = await processTagThroughEdgeFunction(clean);
  const tropeId = matched?.trope_id || await fallbackTropeLookup(clean);
  if (!tropeId) return { trope_id: null, tagType: 'trope', source: 'default' };

  // On a flagged_review the id above is a row this very call caused to be
  // created, so its category is our own 'trope' default coming back. Read the
  // bucket off the tag it was flagged AGAINST instead.
  if (matched?.result_type === 'flagged_review' && matched.possible_match) {
    const near = await bucketForTagName(matched.possible_match);
    if (near) {
      return { trope_id: tropeId, tagType: near, source: 'near-match' };
    }
    // Fall through: the near-match's own category is unmapped or unreadable,
    // which is no better than the default. Reported as 'default', not
    // 'near-match', so a failed borrow never reads as a successful one.
    console.warn(`resolveTagWithBucket: "${clean}" was flagged against "${matched.possible_match}", whose bucket could not be read — filing it as a trope`);
    return { trope_id: tropeId, tagType: 'trope', source: 'default' };
  }

  // The matcher hands back an id, not a category, so the category is read from
  // the row it landed on.
  const { data, error } = await supabase
    .from('tropes')
    .select('category')
    .eq('id', tropeId)
    .maybeSingle();

  if (error) {
    // The tag still saves, under the old default. Say so rather than letting a
    // failed lookup look like a considered answer of 'trope'.
    console.warn(`resolveTagWithBucket: could not read the category for "${clean}" (${error.message}) — filing it as a trope`);
    return { trope_id: tropeId, tagType: 'trope', source: 'default' };
  }

  const tagType = CATEGORY_TO_TAG_TYPE[data?.category];
  if (!tagType) {
    // A category nobody has mapped. Passing it through would fail the CHECK
    // constraint and lose the tag entirely, so it falls back instead.
    if (data?.category) {
      console.warn(`resolveTagWithBucket: "${clean}" has category "${data.category}", which is not one of the eight buckets — filing it as a trope`);
    }
    return { trope_id: tropeId, tagType: 'trope', source: 'default' };
  }

  return { trope_id: tropeId, tagType, source: 'matched' };
}

/**
 * Process a full tags object (the shape returned by AI tagging) and
 * return an array of { trope_id, tag_type, source } ready for book_tags upsert.
 *
 * @param {Object} tags     — { tropes: [], tone: [], genre: [], spice: string }
 * @param {string} source   — 'reader' | 'ai' | 'author'
 */
export async function resolveAllTagsToBookTags(tags, source = 'reader') {
  const bookTags = [];

  const tagGroups = [
    { list: tags.tropes  || tags.trope  || [], type: 'trope' },
    { list: tags.moods   || tags.tone   || [], type: 'tone'  },
    { list: tags.genres  || tags.genre  || [], type: 'genre' },
    { list: tags.warnings || [],               type: 'trigger'},
    // spice is a single value, not an array
    ...(tags.spice
      ? [{ list: [typeof tags.spice === 'string' ? tags.spice : tags.spice[0]], type: 'spice' }]
      : []),
  ];

  for (const group of tagGroups) {
    for (const tagName of group.list) {
      if (!tagName?.trim()) continue;

      const tropeId = await resolveTagToTropeId(tagName, group.type);

      if (tropeId) {
        bookTags.push({
          trope_id: tropeId,
          tag_type: group.type,
          source,
        });
      }
      // If tropeId is null: edge function logged it as new/pending — it'll
      // appear in the admin queue and get linked to books after approval.
    }
  }

  return bookTags;
}
/**
 * Write a reader's genre/subgenre picks into book_genres/book_subgenres,
 * alongside whatever write already lands the same picks in book_tags
 * (insertReaderGenre, insertReaderTropes, or resolveAllTagsToBookTags --
 * whichever the calling surface uses). Sibling to the author portal's
 * writeBookGenres (reanlo-author/src/hooks/useBooks.js) -- PR 3's reader-side
 * half of the same "new doc for genre" PR2 ruling that function implements
 * for authors: the picker dual-writes, book_tags stays live until PR 5,
 * book_genres/book_subgenres is the new path alongside it. Needs no SQL --
 * both tables and their 'reader' source value already exist (PR 1).
 *
 * Deliberately NOT a port of writeBookGenres's remove/insert diff: that
 * function exists to let an author EDIT a book's canonical genre list, which
 * is why it tracks source='author' rows and reports what happened to ones
 * removed from the form. A reader here is only ever ADDING an opinion to
 * somebody else's book -- there is nothing to remove and no author-only
 * ownership to protect against -- so this is a plain additive upsert,
 * source: 'reader', the same shape insertReaderGenre/insertReaderTropes
 * already use for book_tags. ignoreDuplicates means a second reader (or the
 * same reader re-saving) picking a genre another reader already added is a
 * no-op, not an error -- the UNIQUE (book_id, genre_id) / (book_id,
 * subgenre_id) constraints exist for exactly that case.
 *
 * The error is returned, not thrown or logged here -- each caller decides
 * whether and how to surface it. Nothing reads these two tables yet (that
 * starts in PR 5), so a failure here does not change what the reader sees
 * today the way a failed book_tags write would, but callers still differ:
 * some log it, the journal and the CSV import tell the reader.
 *
 * @param {number} bookId
 * @param {number[]} genreIds
 * @param {number[]} subgenreIds
 * @returns {Promise<{error: string|null}>}
 */
export async function writeReaderGenrePicks(bookId, genreIds = [], subgenreIds = []) {
  if (!bookId || (!genreIds.length && !subgenreIds.length)) return { error: null };

  const tasks = [];
  if (genreIds.length) {
    tasks.push(
      supabase.from('book_genres')
        .upsert(
          genreIds.map(genre_id => ({ book_id: bookId, genre_id, source: 'reader' })),
          { onConflict: 'book_id,genre_id', ignoreDuplicates: true }
        )
        .then(({ error }) => (error ? `book_genres: ${error.message}` : null))
    );
  }
  if (subgenreIds.length) {
    tasks.push(
      supabase.from('book_subgenres')
        .upsert(
          subgenreIds.map(subgenre_id => ({ book_id: bookId, subgenre_id, source: 'reader' })),
          { onConflict: 'book_id,subgenre_id', ignoreDuplicates: true }
        )
        .then(({ error }) => (error ? `book_subgenres: ${error.message}` : null))
    );
  }

  const results = await Promise.all(tasks);
  const errors = results.filter(Boolean);
  if (errors.length) console.error('writeReaderGenrePicks:', errors.join('; '));
  return { error: errors.length ? errors.join('; ') : null };
}
